import api, { ApiResponse } from './api';
import type { DaznIdentityDefaults } from '@/utils/dazn-account-form';

export interface DaznSubscription {
  status: string | null;
  plan: string | null;
  expiresAt: string | null;
}

export interface DaznProfileData {
  userId: string | null;
  firstName: string | null;
  lastName: string | null;
  subscription: DaznSubscription;
  entitlements: string[];
  supportedLanguages: string[];
  lastLoginAt: string | null;
}

export interface DaznAccount {
  id: number;
  dazn_email: string | null;
  dazn_password: string | null;
  dazn_device_id: string | null;
  dazn_country: string;
  dazn_language: string;
  dazn_timezone: string;
  dazn_user_agent: string | null;
  dazn_x_dazn_ua: string | null;
  dazn_pin: string | null;
  has_token: boolean;
  token_expires_at: string | null;
  // Profile data from DAZN login
  profile: DaznProfileData | null;
}

export interface UpdateDaznAccountRequest {
  dazn_email?: string;
  dazn_password?: string;
  dazn_device_id?: string;
  dazn_country?: string;
  dazn_language?: string;
  dazn_timezone?: string;
  dazn_user_agent?: string;
  dazn_x_dazn_ua?: string;
  dazn_pin?: string;
}

export interface DaznLoginResponse {
  success: boolean;
  message: string;
}

export interface DaznTokenStatus {
  valid: boolean;
  expiresAt: string | null;
  minutesRemaining: number;
}

export interface DaznProfile {
  email: string;
  name: string;
  country: string;
  [key: string]: unknown;
}

export interface DaznEpgEntry {
  asset_id: string;
  title: string;
  subtitle?: string;
  sport_name?: string;
  competition_name?: string;
  start_time: string;
  end_time: string;
  image?: string;
}

export interface DaznLiveChannel {
  id: string;
  title: string;
  subtitle?: string;
  image?: string;
  is_live: boolean;
}

export interface DaznAccountResponse {
  account: DaznAccount | null;
  hasDefaultProxy: boolean;
  /** Identity strings for a new account (the Android TV user agent); absent from an older server. */
  identityDefaults?: DaznIdentityDefaults;
}

export interface AutoDeviceIdRequest {
  email: string;
  password: string;
  proxy_id: number;
  country: string;
  language?: string;
}

export interface AutoDeviceIdResponse {
  deviceId: string;
}

export interface DaznDevice {
  id: string;
  name: string;
  model: string;
  os: string;
  osVersion: string;
  ipAddress: string;
  registrationDate: string;
  playbackTimestamp: string;
  status: string;
  type: string;
  city: string;
  countrycode: string;
}

export interface DaznSubscriptionDetail {
  brand: string;
  status: string;
  autoRenew: boolean;
  startDate: string;
  termEndDate: string;
  nextPaymentDate: string | null;
  paymentMethod: {
    type: string;
    details: { payerEmail?: string };
  } | null;
  activePass: {
    currency: string;
    price: number;
    period: string;
  } | null;
  currentPlan: string;
  countryOfSubscription: string;
}

/** Status of the logged-in user's own WVD (the file content is never sent to the browser). */
export type WvdStatus =
  | { uploaded: false }
  | {
      uploaded: true;
      originalName: string;
      systemId: number | null;
      securityLevel: number | null;
      uploadedAt: string;
      sharedSystemId: boolean;
      lastStatus: 'ok' | 'rejected' | null;
      lastStatusCode: number | null;
      lastStatusAt: string | null;
    };

/** One row of the admin list: a customer's WVD. */
export interface AdminWvd {
  userId: number;
  username: string;
  email: string;
  originalName: string;
  systemId: number | null;
  securityLevel: number | null;
  uploadedAt: string;
  sharedSystemId: boolean;
  lastStatus: 'ok' | 'rejected' | null;
  lastStatusCode: number | null;
  lastStatusAt: string | null;
}

export const daznService = {
  async getWvd(): Promise<WvdStatus> {
    const response = await api.get<ApiResponse<WvdStatus>>('/dazn/wvd');
    return response.data.data;
  },

  /** Uploads (or replaces) my WVD; the server checks it is a real device file. */
  async uploadWvd(file: File): Promise<WvdStatus> {
    const bytes = new Uint8Array(await file.arrayBuffer());
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    const response = await api.put<ApiResponse<WvdStatus>>('/dazn/wvd', { name: file.name, data: btoa(binary) });
    return response.data.data;
  },

  async deleteWvd(): Promise<void> {
    await api.delete('/dazn/wvd');
  },

  /** Admin: every customer's WVD. */
  async adminListWvds(): Promise<AdminWvd[]> {
    const response = await api.get<ApiResponse<AdminWvd[]>>('/dazn/wvd/admin');
    return response.data.data;
  },

  /** Admin: the file of one customer, to save it. */
  async adminDownloadWvd(userId: number): Promise<Blob> {
    const response = await api.get(`/dazn/wvd/admin/${userId}/download`, { responseType: 'blob' });
    return response.data;
  },

  async getAccount(): Promise<DaznAccountResponse> {
    const response = await api.get<ApiResponse<DaznAccountResponse>>('/dazn/account');
    return response.data.data;
  },

  async updateAccount(data: UpdateDaznAccountRequest): Promise<DaznAccount> {
    const response = await api.put<ApiResponse<DaznAccount>>('/dazn/account', data);
    return response.data.data;
  },

  async login(): Promise<DaznLoginResponse> {
    const response = await api.post<ApiResponse<{ tokenStatus: DaznTokenStatus }>>('/dazn/login');
    // Return success and message from the root response
    return {
      success: response.data.success,
      message: response.data.message || 'Login successful',
    };
  },

  async logout(): Promise<void> {
    await api.post('/dazn/logout');
  },

  async getTokenStatus(): Promise<DaznTokenStatus> {
    const response = await api.get<ApiResponse<DaznTokenStatus>>('/dazn/token-status');
    return response.data.data;
  },

  async getProfile(): Promise<DaznProfile | null> {
    const response = await api.get<ApiResponse<DaznProfile | null>>('/dazn/profile');
    return response.data.data;
  },

  async getEpg(date: string): Promise<DaznEpgEntry[]> {
    const response = await api.get<ApiResponse<DaznEpgEntry[]>>(`/dazn/epg/${date}`);
    return response.data.data;
  },

  async getLiveChannels(): Promise<DaznLiveChannel[]> {
    const response = await api.get<ApiResponse<DaznLiveChannel[]>>('/dazn/live-channels');
    return response.data.data;
  },

  async autoGetDeviceId(data: AutoDeviceIdRequest): Promise<AutoDeviceIdResponse> {
    const response = await api.post<ApiResponse<AutoDeviceIdResponse>>('/dazn/auto-device-id', data);
    return response.data.data;
  },

  async getDevices(): Promise<DaznDevice[]> {
    const response = await api.get<ApiResponse<DaznDevice[]>>('/dazn/devices');
    return response.data.data;
  },

  async disconnectAllDevices(): Promise<void> {
    await api.delete('/dazn/devices');
  },

  async getSubscriptions(): Promise<DaznSubscriptionDetail[]> {
    const response = await api.get<ApiResponse<DaznSubscriptionDetail[]>>('/dazn/subscriptions');
    return response.data.data;
  },
};
